/**
 * Portal Data Prefetching
 * 
 * Preloads frequently used data after login to make the portal feel instant.
 * Data is cached in memory and available immediately when user navigates.
 */
import axios from 'axios';
import apiCache, { CacheTTL } from './apiCache';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Prefetch all common portal data after login
 * Runs in background without blocking the UI
 * 
 * @param {string} token - JWT token from login
 */
export async function prefetchPortalData(token) {
  if (!token) return;

  const tokenSuffix = token.slice(-8);
  const headers = { Authorization: `Bearer ${token}` };

  // Define all endpoints to prefetch with their cache keys and TTLs
  const prefetchItems = [
    {
      key: `profile-${tokenSuffix}`,
      endpoint: '/portal/auth/me',
      ttl: CacheTTL.MEDIUM
    },
    {
      key: `appointments-${tokenSuffix}`,
      endpoint: '/portal/appointments',
      ttl: CacheTTL.SHORT
    },
    {
      key: `packages-${tokenSuffix}`,
      endpoint: '/portal/packages',
      ttl: CacheTTL.MEDIUM
    },
    {
      key: `points-${tokenSuffix}`,
      endpoint: '/portal/points',
      ttl: CacheTTL.MEDIUM
    },
    {
      key: `open-items-${tokenSuffix}`,
      endpoint: '/portal/open-items',
      ttl: CacheTTL.SHORT
    },
    {
      key: `payment-methods-${tokenSuffix}`,
      endpoint: '/portal/payment-methods',
      ttl: CacheTTL.LONG
    }
  ];

  // Prefetch all in parallel (fire and forget)
  const prefetchPromises = prefetchItems.map(async ({ key, endpoint, ttl }) => {
    // Skip if already cached
    if (apiCache.get(key)) {
      return;
    }

    try {
      const response = await axios.get(`${API}${endpoint}`, { headers });
      apiCache.set(key, response.data, ttl);
    } catch (error) {
      // Silently fail - prefetch is optional optimization
      console.debug(`Prefetch skipped for ${endpoint}:`, error.message);
    }
  });

  // Run all prefetches in parallel
  await Promise.allSettled(prefetchPromises);
}

/**
 * Clear all cached portal data (e.g., on logout)
 */
export function clearPortalCache() {
  apiCache.invalidateContaining('/portal/');
  apiCache.clear();
}

/**
 * Get prefetch statistics for debugging
 */
export function getPrefetchStats() {
  return apiCache.getStats();
}

export default prefetchPortalData;
